import { ActionIcon, Drawer, Group, Text } from '@mantine/core';
import { IconMinus, IconX } from '@tabler/icons-react';
import { useUploadStore } from '../uploadStore';
import { useUploadEngine } from '../useUploadEngine';
import { UploadDropzone } from './UploadDropzone';
import { UploadFileList } from './UploadFileList';

/**
 * Right-side upload drawer for regular users. Pure consumer of the
 * UploadEngineProvider — queue state lives in the Zustand store and the Uppy
 * instance in the provider, so closing/unmounting the drawer never interrupts
 * in-flight uploads.
 */
export function UploadDrawer() {
  const drawerOpen = useUploadStore((s) => s.drawerOpen);
  const closeDrawer = useUploadStore((s) => s.closeDrawer);
  const minimizeDrawer = useUploadStore((s) => s.minimizeDrawer);
  const items = useUploadStore((s) => s.items);

  const {
    canIngest,
    effectiveCId,
    ingestcfgs,
    selectedIngestcfgId,
    setSelectedIngestcfgId,
    hasMultiple,
    isRestricted,
    addFilesWithMeta,
    cancelUpload,
    retryUpload,
  } = useUploadEngine();

  const activeCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading' || i.status === 'processing'
  ).length;

  if (!canIngest || effectiveCId == null) {
    return null;
  }

  return (
    <Drawer
      opened={drawerOpen}
      onClose={closeDrawer}
      position="right"
      size="lg"
      withCloseButton={false}
      styles={{
        body: { display: 'flex', flexDirection: 'column', height: '100%', padding: 'var(--mantine-spacing-md)' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      <Group justify="space-between" mb="md">
        <Text fw={600} size="lg">
          Upload Videos
          {activeCount > 0 && (
            <Text span size="sm" c="dimmed" ml="xs">
              ({activeCount} active)
            </Text>
          )}
        </Text>
        <Group gap={4}>
          <ActionIcon variant="subtle" color="gray" onClick={minimizeDrawer} aria-label="Minimize">
            <IconMinus size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" color="gray" onClick={closeDrawer} aria-label="Close">
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <UploadDropzone
        addFilesWithMeta={addFilesWithMeta}
        ingestcfgs={ingestcfgs}
        selectedIngestcfgId={selectedIngestcfgId}
        onSelectIngestcfg={setSelectedIngestcfgId}
        hasMultiple={hasMultiple}
        isRestricted={isRestricted}
        cId={effectiveCId}
        hasFiles={items.length > 0}
      />

      <UploadFileList onCancel={cancelUpload} onRetry={retryUpload} />
    </Drawer>
  );
}
